// Transcript: words in speaker paragraphs. Click a word to play from it;
// the spoken word is highlighted while the episode plays. Select words and
// press Delete to cut them from the audio (Edits.tsx renders the result).
// Lines can be corrected, speakers named, and the transcript exported.

import { memo, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import {
  activeWordIndex,
  clock,
  cutForWords,
  isWordCut,
  downloadTranscript,
  speakerNames,
  toParagraphs,
  useLatestTranscription,
  useRenameSpeaker,
  useStartTranscription,
  useTranscript,
  useUpdateSegment,
  useWords,
  type Paragraph,
  type Transcription,
  type Word,
} from '../../lib/transcripts';
import { useMediaFile } from '../../lib/shows';
import { EditBar, useEdits, useSkipCuts } from './Edits';
import { errorInfo } from '../../lib/api';
import { Button } from '../ui/Button';
import { EmptyState, ErrorMessage, Loading } from '../ui/Feedback';
import { useToast } from '../ui/Toast';
import { usePlayer } from './Player';

export function TranscriptPanel({ mediaFileId, episodeId }: { mediaFileId: string | null; episodeId: string }) {
  const latest = useLatestTranscription(mediaFileId);
  const start = useStartTranscription(mediaFileId);
  const toast = useToast();
  const tx = latest.data;

  const transcribe = () =>
    start.mutate(undefined, {
      onSuccess: () => toast.show('Transcribing — this takes about a minute per 10 minutes of audio.', 'info'),
      onError: (err) => toast.show(errorInfo(err).message, 'error'),
    });

  return (
    <section aria-labelledby="transcript-heading" className="space-y-4 rounded-xl border border-border p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="transcript-heading" className="text-lg font-semibold">
          Transcript
        </h2>
        {tx?.status === 'completed' && <Exports id={tx.id} />}
      </div>

      {!mediaFileId && <p className="text-sm text-muted">Attach audio to transcribe this episode.</p>}
      {mediaFileId && latest.isLoading && <Loading />}
      {latest.isError && <ErrorMessage error={latest.error} onRetry={() => latest.refetch()} />}
      {mediaFileId && latest.data === null && (
        <EmptyState
          title="No transcript yet"
          body="Transcribe to get word-accurate text with speakers, then run the AI tools and export captions."
          action={
            <Button loading={start.isPending} onClick={transcribe}>
              Transcribe
            </Button>
          }
        />
      )}
      {(tx?.status === 'pending' || tx?.status === 'processing') && (
        <p role="status" className="text-sm text-muted">
          Transcribing… it appears here when it’s done.
        </p>
      )}
      {tx?.status === 'failed' && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-danger-soft p-3 text-sm text-danger">
          <span>Transcription failed. The Activity page has the reason.</span>
          <Button size="sm" variant="secondary" loading={start.isPending} onClick={transcribe}>
            Try again
          </Button>
        </div>
      )}
      {tx?.status === 'completed' && <TranscriptBody transcription={tx} episodeId={episodeId} />}
    </section>
  );
}

function Exports({ id }: { id: string }) {
  const toast = useToast();
  return (
    <div className="flex items-center gap-1 text-sm">
      <span className="text-muted">Download</span>
      {(['srt', 'vtt', 'txt'] as const).map((f) => (
        <button
          key={f}
          type="button"
          className="rounded px-2 py-0.5 font-medium text-accent hover:bg-accent-soft"
          onClick={() =>
            downloadTranscript(id, f).catch(() => toast.show('Could not download the transcript', 'error'))
          }
        >
          {f.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/** The words a DOM selection covers, by their data-word-id spans. */
export function selectedWords(container: HTMLElement, byId: Map<string, Word>): Word[] {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return [];
  const range = sel.getRangeAt(0);
  return [...container.querySelectorAll<HTMLElement>('[data-word-id]')]
    .filter((el) => range.intersectsNode(el))
    .map((el) => byId.get(el.dataset.wordId!))
    .filter((w): w is Word => !!w);
}

function TranscriptBody({ transcription, episodeId }: { transcription: Transcription; episodeId: string }) {
  const transcript = useTranscript(transcription.id);
  const words = useWords(transcription.id);
  const media = useMediaFile(transcription.mediaFileId);
  const player = usePlayer();
  const [query, setQuery] = useState('');
  const edits = useEdits(media.data?.editCuts ?? []);
  const [preview, setPreview] = useState(true);
  const [selection, setSelection] = useState<Word[]>([]);
  const box = useRef<HTMLDivElement>(null);
  useSkipCuts(edits.cuts, preview);

  const paragraphs = useMemo(
    () => (transcript.data && words.data ? toParagraphs(transcript.data.segments, words.data) : []),
    [transcript.data, words.data],
  );
  const names = useMemo(() => speakerNames(transcript.data?.segments ?? []), [transcript.data]);
  const allWords = words.data ?? [];
  const byId = useMemo(() => new Map((words.data ?? []).map((w) => [w.id, w])), [words.data]);
  const active = activeWordIndex(allWords, player.currentMs);
  const activeId = active >= 0 ? allWords[active]!.id : null;

  if (transcript.isLoading || words.isLoading) return <Loading />;
  if (transcript.isError || words.isError)
    return <ErrorMessage error={transcript.error ?? words.error} onRetry={() => void transcript.refetch()} />;
  if (paragraphs.length === 0) return <p className="text-sm text-muted">No speech was found in this audio.</p>;

  const readSelection = () => setSelection(box.current ? selectedWords(box.current, byId) : []);
  const cutSelection = () => {
    const cut = cutForWords(selection);
    if (!cut) return;
    edits.apply([...edits.cuts, cut]);
    window.getSelection()?.removeAllRanges();
    setSelection([]);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if ((e.key === 'Delete' || e.key === 'Backspace') && selection.length > 0) {
      e.preventDefault();
      cutSelection();
    }
  };
  const restore = (w: Word) => {
    const mid = (w.startMs + w.endMs) / 2;
    edits.apply(edits.cuts.filter((c) => !(mid >= c.startMs && mid < c.endMs)));
  };

  const q = query.trim().toLowerCase();
  const matches = q ? paragraphs.filter((p) => p.text.toLowerCase().includes(q)).length : 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find in transcript"
          aria-label="Find in transcript"
          className="w-64 max-w-full rounded-md border border-border bg-bg px-3 py-1.5 text-sm"
        />
        {q && (
          <span className="text-xs text-muted" role="status">
            {matches === 1 ? '1 line' : `${matches} lines`}
          </span>
        )}
        <span className="ml-auto text-xs text-muted">
          {transcription.wordCount?.toLocaleString() ?? allWords.length} words · click any word to play from there
        </span>
      </div>
      <Speakers transcriptionId={transcription.id} names={names} />
      {media.data && (
        <EditBar
          media={media.data}
          transcriptionId={transcription.id}
          episodeId={episodeId}
          edits={edits}
          preview={preview}
          onPreview={setPreview}
        />
      )}
      {selection.length > 0 && (
        <div className="sticky top-0 z-10 flex items-center gap-2 rounded-md bg-accent-soft px-3 py-1.5 text-sm">
          <span>
            {selection.length} {selection.length === 1 ? 'word' : 'words'} selected
          </span>
          <Button size="sm" onClick={cutSelection}>
            Cut selection
          </Button>
        </div>
      )}
      <div
        ref={box}
        tabIndex={0}
        role="group"
        aria-label="Transcript text"
        onMouseUp={readSelection}
        onKeyUp={readSelection}
        onKeyDown={onKeyDown}
        className="max-h-[36rem] space-y-1 overflow-y-auto pr-2 focus:outline-none"
      >
        {paragraphs.map((p, i) => {
          const hit = !q || p.text.toLowerCase().includes(q);
          if (!hit) return null;
          const prev = paragraphs[i - 1];
          return (
            <ParagraphView
              key={p.id}
              paragraph={p}
              transcriptionId={transcription.id}
              speaker={p.speakerId !== null ? (names.get(p.speakerId) ?? null) : null}
              showSpeaker={!prev || prev.speakerId !== p.speakerId || !!q}
              activeWordId={activeId && p.words.some((w) => w.id === activeId) ? activeId : null}
              cutWordIds={p.words
                .filter((w) => isWordCut(w, edits.cuts))
                .map((w) => w.id)
                .join(',')}
              onSeek={player.seek}
              onRestore={restore}
            />
          );
        })}
      </div>
    </div>
  );
}

function Speakers({ transcriptionId, names }: { transcriptionId: string; names: Map<string, string> }) {
  const rename = useRenameSpeaker(transcriptionId);
  const toast = useToast();
  const [editing, setEditing] = useState<string | null>(null);
  const [value, setValue] = useState('');
  if (names.size === 0) return null;

  const save = (e: FormEvent, speakerId: string) => {
    e.preventDefault();
    rename.mutate(
      { speakerId, name: value },
      {
        onSuccess: () => setEditing(null),
        onError: (err) => toast.show(errorInfo(err).message, 'error'),
      },
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="text-muted">Speakers</span>
      {[...names].map(([id, name]) =>
        editing === id ? (
          <form key={id} onSubmit={(e) => save(e, id)} className="flex items-center gap-1">
            <input
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-label={`Name for ${name}`}
              maxLength={80}
              className="w-32 rounded border border-border bg-bg px-2 py-0.5"
            />
            <Button size="sm" type="submit" loading={rename.isPending}>
              Save
            </Button>
            <Button size="sm" variant="ghost" type="button" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </form>
        ) : (
          <button
            key={id}
            type="button"
            title="Rename speaker"
            onClick={() => {
              setEditing(id);
              setValue(name.startsWith('Speaker ') ? '' : name);
            }}
            className="rounded-full bg-surface px-2.5 py-0.5 font-medium text-text hover:bg-accent-soft"
          >
            {name} ✎
          </button>
        ),
      )}
    </div>
  );
}

interface ParagraphProps {
  paragraph: Paragraph;
  transcriptionId: string;
  speaker: string | null;
  showSpeaker: boolean;
  activeWordId: string | null;
  /** Comma-separated ids of this paragraph's cut words (a string keeps memo cheap). */
  cutWordIds: string;
  onSeek: (ms: number) => void;
  onRestore: (w: Word) => void;
}

// Memoized: while playing, only the paragraph holding the spoken word re-renders.
const ParagraphView = memo(function ParagraphView({
  paragraph: p,
  transcriptionId,
  speaker,
  showSpeaker,
  activeWordId,
  cutWordIds,
  onSeek,
  onRestore,
}: ParagraphProps) {
  const cut = new Set(cutWordIds ? cutWordIds.split(',') : []);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(p.text);
  const update = useUpdateSegment(transcriptionId);
  const toast = useToast();

  const save = (e: FormEvent) => {
    e.preventDefault();
    update.mutate(
      { segmentId: p.id, text: text.trim() },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => toast.show(errorInfo(err).message, 'error'),
      },
    );
  };

  return (
    <div className="group grid grid-cols-[3.5rem_minmax(0,1fr)] gap-3 rounded-md py-1 [content-visibility:auto] hover:bg-surface">
      <button
        type="button"
        onClick={() => onSeek(p.startMs)}
        className="pt-0.5 text-right text-xs tabular-nums text-muted hover:text-accent"
        aria-label={`Play from ${clock(p.startMs)}`}
      >
        {clock(p.startMs)}
      </button>
      <div>
        {showSpeaker && speaker && <div className="text-xs font-semibold text-text">{speaker}</div>}
        {editing ? (
          <form onSubmit={save} className="space-y-2">
            <textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              aria-label="Correct this line"
              rows={Math.min(6, Math.ceil(text.length / 80) + 1)}
              className="w-full rounded-md border border-border bg-bg p-2 text-sm"
            />
            <div className="flex gap-2">
              <Button size="sm" type="submit" loading={update.isPending} disabled={!text.trim()}>
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                type="button"
                onClick={() => {
                  setText(p.text);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <p className="text-sm leading-relaxed text-text">
            {p.edited || p.words.length === 0 ? (
              <span>{p.text}</span>
            ) : (
              p.words.map((w) => (
                <span key={w.id}>
                  {cut.has(w.id) ? (
                    <span
                      role="button"
                      tabIndex={-1}
                      data-word-id={w.id}
                      title="Cut — click to restore"
                      onClick={() => onRestore(w)}
                      className="cursor-pointer rounded-sm text-muted line-through decoration-danger opacity-60 hover:opacity-100"
                    >
                      {w.text}
                    </span>
                  ) : (
                    <span
                      role="button"
                      tabIndex={-1}
                      data-word-id={w.id}
                      onClick={() => onSeek(w.startMs)}
                      className={`cursor-pointer rounded-sm hover:bg-accent-soft ${w.id === activeWordId ? 'bg-accent text-accent-contrast' : ''}`}
                    >
                      {w.text}
                    </span>
                  )}{' '}
                </span>
              ))
            )}
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="ml-1 text-xs text-muted opacity-0 hover:text-accent focus:opacity-100 group-hover:opacity-100"
            >
              Edit
            </button>
          </p>
        )}
      </div>
    </div>
  );
});
